import { AssistanceStep } from '@sec-loans-ui/common/steps/assistance-step/AssistanceStep'
import { LoanCatalogStep } from '@sec-loans-ui/common/steps/loan-catalog-step/LoanCatalogStep'
import { ApplicationSelectData, AuthenticatedContext } from '@sec-loans-ui/common/types'
import { Wizard as IfpLoansWizard } from '@sec-loans-ui/ifp-loans/wizard/Wizard'
import { Wizard as LoanOfferWizard } from '@sec-loans-ui/loan-offer/wizard/Wizard'
import { ApplicationWizardProvider } from '@sec-loans-ui/state/applicationWizardContext'
import {
  getAndStoreInputParams,
  getAuthenticationMode,
  getSecLoansFlow,
  getSecLoansSourceChannel,
} from '@sec-loans-ui/utils/helpers'
import { Wizard as SecLoansWizard } from '@sec-loans-ui/wizard/Wizard'
import { assertValue } from '@shared/helpers'
import {
  ENDPOINT_SEC_LOANS_GET_USER_INFO,
  ENDPOINT_SEC_LOANS_VERIFY_AUTH_CODE,
  SecLoansCreateApplicationResponse,
  SecLoansSource,
} from '@shared/sec-loans'
import { AppId, AppName } from '@shared/types'
import { CommonWizardWrapper } from '@ui-common/components/CommonWizardWrapper'
import { AuthenticateStep } from '@ui-common/steps/AuthenticateStep'
import { AuthenticationCancelled } from '@ui-common/steps/AuthenticateStep/types'
import { GetUserInfoStep } from '@ui-common/steps/GetUserInfoStep/GetUserInfoStep'
import { InitializeStep } from '@ui-common/steps/InitializeStep'
import { getSourceChannel } from '@ui-common/utils/helpers'
import { callEndpoint } from '@ui-common/utils/http/client'
import { getLanguage } from '@ui-common/utils/language'

import { OnCreatedIfpApplicationData } from '../../ifp-loans/types'
import { EntranceStep } from '../../wizard/steps/entrance-step/EntranceStep'
import { WizardStep } from './rootWizardMachine'
import { useWizardLogic } from './useWizardLogic'

export const RootWizard = ({ authenticatedContext }: { authenticatedContext?: AuthenticatedContext }) => {
  const language = getLanguage()
  const {
    analyticsService,
    send,
    authToken,
    user,
    step,
    secLoansEntryContext,
    ifpLoansEntryContext,
    loanOfferEntryContext,
    entranceStepMachineActor,
    showAbortPromptModal,
    onAbort,
  } = useWizardLogic(authenticatedContext)

  const onApplicationSelect = (applicationSelectData: ApplicationSelectData) => {
    send({ type: 'SELECT_APP', ...applicationSelectData })
  }

  const onCreatedSecLoansApplication = (data: SecLoansCreateApplicationResponse) => {
    send({ type: 'CREATED_SEC_LOANS_APPLICATION', data })
  }

  const onCreatedIfpLoansApplication = (data: OnCreatedIfpApplicationData) => {
    send({ type: 'CREATED_IFP_LOANS_APPLICATION', data })
  }

  const onGoToApplicationsClick = () => send({ type: 'GO_TO_CATALOG' })

  const onNewApplicationSelect = () => send({ type: 'GO_TO_ASSISTANCE' })

  const stepComponentMap: Partial<Record<WizardStep, () => React.ReactNode>> = {
    [WizardStep.Initialize]: () => (
      <InitializeStep
        appName={AppName.SecLoans}
        shouldGetConfig={true}
        actions={{
          getAndStoreInputParams,
          onInitialized: (data) => send({ type: 'NEXT_STEP', data }),
          onInitializedExit: onAbort,
        }}
      />
    ),
    [WizardStep.Entrance]: () => (
      <EntranceStep
        service={assertValue(entranceStepMachineActor, 'entranceStepMachineActor')}
        showAbortPromptModal={onAbort}
        showProcessingTimeNotification={secLoansEntryContext?.params?.SHOW_PROCESSING_TIME_NOTIFICATION === 'true'}
      />
    ),
    [WizardStep.Authenticate]: () => (
      <AuthenticateStep
        verifyAuthCodeCallback={(authCode) => {
          const sourceChannel = getSourceChannel<SecLoansSource>()
          return callEndpoint(ENDPOINT_SEC_LOANS_VERIFY_AUTH_CODE, {
            requestPayload: {
              authCode,
              uiLanguage: language,
              sourceChannel,
              flow: getSecLoansFlow(true),
            },
          })
        }}
        authMode={getAuthenticationMode(getSecLoansSourceChannel())}
        onExit={onAbort}
        onAuthenticated={(token) => send({ type: 'AUTHENTICATION_DONE', data: { authToken: token } })}
        onAuthenticationCancelled={() => send({ type: AuthenticationCancelled })}
      />
    ),
    [WizardStep.GetUserInfo]: () => (
      <GetUserInfoStep
        onAbort={showAbortPromptModal}
        getUserInfo={() => callEndpoint(ENDPOINT_SEC_LOANS_GET_USER_INFO)}
        onGetUserInfoDone={(data) => send({ type: 'GET_DATA_DONE', data })}
      />
    ),
    [WizardStep.Assistance]: () => (
      <AssistanceStep
        onCreatedSecLoansApplication={onCreatedSecLoansApplication}
        onCreatedIfpLoansApplication={onCreatedIfpLoansApplication}
        onGoToApplicationsClick={onGoToApplicationsClick}
        onAbort={showAbortPromptModal}
        user={assertValue(user, 'user')}
      />
    ),
    [WizardStep.LoanCatalog]: () => (
      <LoanCatalogStep
        onApplicationSelect={onApplicationSelect}
        onNewApplicationSelect={onNewApplicationSelect}
        onAbort={showAbortPromptModal}
        user={assertValue(user, 'user')}
      />
    ),
    [WizardStep.GoToIfpLoans]: () => (
      <IfpLoansWizard initialContext={assertValue(ifpLoansEntryContext, 'ifpLoansEntryContext')} />
    ),
    [WizardStep.GoToLoanOffer]: () => (
      <LoanOfferWizard
        initialContext={{
          user: assertValue(user, 'user'),
          loanOffer: assertValue(loanOfferEntryContext?.loanOffer, `loanOfferEntryContext.loanOffer`),
        }}
      />
    ),
    [WizardStep.GoToSecLoans]: () => (
      <ApplicationWizardProvider initialContext={secLoansEntryContext}>
        <SecLoansWizard />
      </ApplicationWizardProvider>
    ),
  }

  switch (step) {
    case WizardStep.GoToIfpLoans:
      return <IfpLoansWizard initialContext={assertValue(ifpLoansEntryContext, 'ifpLoansEntryContext')} />
    case WizardStep.GoToSecLoans:
      return (
        <ApplicationWizardProvider initialContext={secLoansEntryContext}>
          <SecLoansWizard />
        </ApplicationWizardProvider>
      )
    default:
      return (
        <CommonWizardWrapper
          stepComponentMap={stepComponentMap}
          step={step}
          authToken={authToken ?? null}
          appId={AppId.SecLoans}
          analyticsService={analyticsService}
          onLogout={onAbort}
          userContext={null}
        />
      )
  }
}
